import {
  Button,
  HStack,
  SkeletonText,
  Stack,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { Card } from '~/components/Card';
import { Heading } from '~/components';
import { BeforeDueDate } from '~/pages/Portal/Settings/NonPayment/BeforeDueDate';
import { DueDate, NonPaymentTypes } from '~/types/nonPayment';
import { AfterDueDate } from '~/pages/Portal/Settings/NonPayment/AfterDueDate';
import api from '~/utils/api';
import { queryClient } from '~/utils/react-query';
import { getAllEmailsDueDate } from '~/services/NonPayment';
import { isUUID } from '~/utils/uuid';
import { Whitelist } from '~/pages/Portal/Settings/NonPayment/Whitelist';

export const NonPayment: React.VFC = () => {
  const initialValues: DueDate = {
    id: uuid(),
    enviar_email_em_dias: undefined,
    repetir_email_em_dias: undefined,
    limite_repeticao_email_em_dias: undefined,
    status: true,
    tipo: NonPaymentTypes.ANTES_DO_VENCIMENTO,
  };
  const [listaAntesVencimento, setLista] = useState<any[]>([initialValues]);
  const [depoisVencimento, setDepoisVencimento] = useState<DueDate>();
  const [protestoValor, setProtestoValor] = useState<DueDate>();
  const [isFetching, setIsFetching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    setIsFetching(true);
    getAllEmailsDueDate()
      .then(response => {
        if (response.responseFirst.data.length) {
          setLista(response.responseFirst.data);
        } else {
          const auxInicial = { ...initialValues };
          auxInicial.enviar_email_em_dias = 7;
          setLista([auxInicial]);
        }
        setDepoisVencimento(response.responseSecond.data[0]);
        setProtestoValor(response.responseThird.data[0]);
      })
      .finally(() => {
        setIsFetching(false);
      });
  }, []);

  const alterarDias = (id: string, valor: string) => {
    const auxNovoValor = { ...initialValues };
    auxNovoValor.id = id;
    auxNovoValor.enviar_email_em_dias = Number(valor);
    const auxLista = listaAntesVencimento?.map(item => {
      if (item.id === id) {
        return auxNovoValor;
      }
      return item;
    });
    setLista(auxLista);
    enviarDados(auxNovoValor);
  };

  const excluirDaLista = (id: string) => {
    const auxLista = listaAntesVencimento?.filter(item => item.id !== id);
    setLista(auxLista);
    excluirDados(id);
  };

  const adicionarNaLista = () => {
    const auxLista = [...listaAntesVencimento];
    auxLista.push(initialValues);
    setLista(auxLista);
  };
  const contentBeforeDueDate = () => {
    return (
      <Stack gridGap={3} py={3} px={2}>
        <HStack justifyContent="space-between">
          <Heading fontSize="16px">Antes do vencimento</Heading>
          <Button
            variant="outline"
            colorScheme="primary"
            size="sm"
            onClick={adicionarNaLista}
            isDisabled={isLoading}
          >
            Adicionar
          </Button>
        </HStack>
        {listaAntesVencimento?.map((item, index) => (
          <BeforeDueDate
            key={`${item.id + uuid()}`}
            id={item.id}
            valor={item.enviar_email_em_dias}
            onChange={alterarDias}
            excluir={excluirDaLista}
            podeExcluir={index !== 0}
          />
        ))}
      </Stack>
    );
  };

  async function enviarDados(obj: DueDate) {
    setIsLoading(true);
    let request;
    if (obj.id && isUUID(obj.id)) {
      const auxObj = { ...obj, id: undefined };
      request = api.post('/controle_inadimplencia', auxObj);
    } else {
      request = api.put(`/controle_inadimplencia/${obj.id}`, obj);
    }
    request
      .then(res => {
        if (obj.tipo === NonPaymentTypes.ANTES_DO_VENCIMENTO) {
          const auxLista = listaAntesVencimento?.map(item => {
            if (item.id === obj.id) {
              return res.data;
            }
            return item;
          });
          setLista(auxLista);
        } else if (obj.tipo === NonPaymentTypes.DEPOIS_DO_VENCIMENTO) {
          setDepoisVencimento(res.data);
        } else {
          setProtestoValor(res.data);
        }
        toast({
          title: `Alterado com sucesso!`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        queryClient.invalidateQueries('beforeDueDate');
        queryClient.invalidateQueries('afterDueDate');
      })
      .catch(e => {
        toast({
          title: `${e.response.data.message}`,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => setIsLoading(false));
  }

  async function excluirDados(id: string) {
    if (isUUID(id)) {
      return;
    }
    setIsLoading(true);
    const request = api.delete(`/controle_inadimplencia/${id}`);
    request
      .then(() => {
        toast({
          title: `Deletado com sucesso!`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        queryClient.invalidateQueries('beforeDueDate');
        queryClient.invalidateQueries('afterDueDate');
      })
      .catch(e => {
        toast({
          title: `${e.response.data.message}`,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => setIsLoading(false));
  }

  const contentAfterDueDate = () => {
    return (
      <Stack gridGap={5} py={3} px={2}>
        <AfterDueDate
          depoisVenc={depoisVencimento}
          protesto={protestoValor}
          onChange={obj => enviarDados(obj)}
        />
      </Stack>
    );
  };

  const contentLoading = () => {
    return (
      <Stack w="80%" gridGap={5} py={3} px={2}>
        <SkeletonText mt="4" noOfLines={5} spacing="5" />
      </Stack>
    );
  };

  return (
    <>
      <Stack gridGap={4} px="8" py="4">
        <Heading>Controle de Inadimplência</Heading>
        {!isFetching && (
          <>
            <Card content={contentBeforeDueDate()} width="80%" />
            <Card content={contentAfterDueDate()} width="80%" />
            <Stack gridGap={5} py={3} px={2}>
              <Whitelist />
            </Stack>
          </>
        )}
        {isFetching && (
          <>
            <Card content={contentLoading()} width="80%" />
            <Card content={contentLoading()} width="80%" />
            <Card content={contentLoading()} width="80%" />
          </>
        )}
      </Stack>
    </>
  );
};
